import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import DateTimePicker from '../../ListaDemandas/FiltroListaDemandas/DateTimePicker';
import toast from '../../../services/toast';
import api from '../../../services/api';
import { IconTrash } from '../../icons';
import {
  StepContainer,
  StepContent,
  SectionContainer,
  SectionTitle,
  SectionDescription,
  FieldsRow,
  FieldGroup,
  Label,
  ErrorMessage,
  TextArea,
  FilesContainer,
  FilesList,
  FileRow,
  FileName,
  AddFilesButton,
  HiddenFileInput,
  DateTimeContainer,
  DatePickerContainer,
  TimezoneReadonly,
  UploadIcon,
  DeleteButton,
  LoadingFileRow,
  LoadingFileInfo,
  LoadingFileName,
  LoadingFileSize,
  LoadingSpinner,
  Spinner,
} from './styles';
import { HeaderContent, StepHeader, StepTitle } from '../styles';
import { formatFileSize } from '../../../utility/byteSizeUtils';

// ===== TIPAGENS DO STEP 4 (Demanda) =====
export type TimezoneCode = 'FNT' | 'BRT' | 'AMT' | 'ACT';

export interface ArquivoAnexoItem {
  nome: string;
  tamanhoBytes: number;
  arquivo_final?: 'sim' | 'não';
  file?: File;
  caminho?: string;
  tempFolder?: string;
  id_proposta_arquivo_original?: number;
}

export interface DadosFinalValores {
  prazo_aceite: string | null; // ISO com offset, ex.: 2025-08-25T17:38:00-03:00
  prazo_para_conclusao: string | null; // ISO com offset
  arquivos: ArquivoAnexoItem[];
  orientacoes: string;
}

export interface DadosFinalProps {
  step: number;
  currentStep: number;
  setDadosFinal: (dados: DadosFinalValores) => void;
  setCurrentStep: (step: number) => void;
  validando: boolean;
  setValidando: (validando: boolean) => void;
  fusoSelecionado?: string;
  prazoFatal?: Date;
  onUploadingChange?: (isUploading: boolean) => void;
  initialDadosFinal?: DadosFinalValores;
  userProfile?: string;
}

// Helpers de timezone para exibir sigla
const timezoneLabel = (tz: string | undefined) => {
  switch (tz) {
    case 'FNT': return 'FNT (UTC-2) - Fernando de Noronha';
    case 'BRT': return 'BRT (UTC-3) - Brasília';
    case 'AMT': return 'AMT (UTC-4) - Manaus';
    case 'ACT': return 'ACT (UTC-5) - Rio Branco';
    default: return tz || '';
  }
};

// Helper para exibir apenas a sigla do timezone
const timezoneShort = (tz: string | undefined) => {
  switch (tz) {
    case 'FNT': return 'FNT';
    case 'BRT': return 'BRT';
    case 'AMT': return 'AMT';
    case 'ACT': return 'ACT';
    default: return tz || 'BRT';
  }
};


const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50MB

const parseInitialDate = (dateStr: string | null | undefined): { data: string; hora: string } => {
  if (!dateStr) return { data: '', hora: '' };
  try {
    const date = new Date(dateStr);
    const data = date.toISOString().split('T')[0];
    const hora = date.toTimeString().split(' ')[0].substring(0, 5);
    return { data, hora };
  } catch {
    return { data: '', hora: '' };
  }
};

const DadosFinal: React.FC<DadosFinalProps> = ({ 
  step, 
  currentStep, 
  setDadosFinal,
  setCurrentStep,
  validando,
  setValidando,
  fusoSelecionado,
  prazoFatal,
  onUploadingChange,
  initialDadosFinal,
  userProfile
}) => {
  const isActive = currentStep === step;

  const initialAceite = parseInitialDate(initialDadosFinal?.prazo_aceite || null);
  const initialConclusao = parseInitialDate(initialDadosFinal?.prazo_para_conclusao || null);

  const [dataAceite, setDataAceite] = useState<string>(initialAceite.data);
  const [horaAceite, setHoraAceite] = useState<string>(initialAceite.hora);
  const [dataConclusao, setDataConclusao] = useState<string>(initialConclusao.data);
  const [horaConclusao, setHoraConclusao] = useState<string>(initialConclusao.hora);
  const [orientacoes, setOrientacoes] = useState<string>(initialDadosFinal?.orientacoes || '');
  const [arquivos, setArquivos] = useState<ArquivoAnexoItem[]>(initialDadosFinal?.arquivos || []);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [arquivosEnviando, setArquivosEnviando] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [sessionId] = useState(() => {
    return `demanda_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  });

  useEffect(() => {
    if (initialDadosFinal) {
      const aceite = parseInitialDate(initialDadosFinal.prazo_aceite);
      const conclusao = parseInitialDate(initialDadosFinal.prazo_para_conclusao);
      setDataAceite(aceite.data);
      setHoraAceite(aceite.hora);
      setDataConclusao(conclusao.data);
      setHoraConclusao(conclusao.hora);
      setOrientacoes(initialDadosFinal.orientacoes || '');
      setArquivos(initialDadosFinal.arquivos || []);
    } else {
      setHoraAceite('');
      setHoraConclusao('');
    }
  }, [initialDadosFinal]);

  // Propaga estrutura consolidada ao pai
  const valores: DadosFinalValores = useMemo(() => {
    const prazo_aceite = userProfile !== 'Solicitante' && dataAceite && horaAceite && fusoSelecionado
      ? dataAceite + 'T' + horaAceite + ':00'
      : null;

    const prazo_para_conclusao = dataConclusao && horaConclusao && fusoSelecionado
      ? dataConclusao + 'T' + horaConclusao + ':00'
      : null;

    return { prazo_aceite, prazo_para_conclusao, arquivos, orientacoes };
  }, [dataAceite, horaAceite, dataConclusao, horaConclusao, fusoSelecionado, arquivos, orientacoes, userProfile]);

  useEffect(() => {
    setDadosFinal(valores);
  }, [valores, setDadosFinal]);

  const onChangeAceite = useCallback((dataStr: string, timeStr: string) => {
    setDataAceite(dataStr);
    setHoraAceite(timeStr);
  }, []);

  const onChangeConclusao = useCallback((dataStr: string, timeStr: string) => {
    setDataConclusao(dataStr);
    setHoraConclusao(timeStr);
  }, []);

  const valorAceiteCompleto = useMemo(() => {
    if (!dataAceite) return '';
    if (!horaAceite || horaAceite === '--:--') return dataAceite;
    return `${dataAceite} ${horaAceite}`;
  }, [dataAceite, horaAceite]);

  const valorConclusaoCompleto = useMemo(() => {
    if (!dataConclusao) return '';
    if (!horaConclusao || horaConclusao === '--:--') return dataConclusao;
    return `${dataConclusao} ${horaConclusao}`;
  }, [dataConclusao, horaConclusao]);

  // Upload múltiplo com validação de 50MB por arquivo; mantém botão sempre visível (RN36.3)
  const handleFiles = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) {
      return;
    }

    const errosLocal: string[] = [];
    const arquivosValidos: File[] = [];

    // Separa arquivos válidos e inválidos
    for (const file of Array.from(fileList)) {
      if (file.size > MAX_FILE_BYTES) {
        errosLocal.push(`Arquivo "${file.name}" excede 50MB.`);
      } else if (arquivos.some(a => a.nome === file.name)) {
        errosLocal.push(`Arquivo "${file.name}" já foi adicionado.`);
      } else {
        arquivosValidos.push(file);
      }
    }

    // Exibe erros dos arquivos inválidos
    if (errosLocal.length > 0) {
      errosLocal.forEach(msg => toast.error(msg));
    }

    // Processa apenas os arquivos válidos
    if (arquivosValidos.length > 0) {
      setIsUploading(true);
      setArquivosEnviando(arquivosValidos);
      const enviados: ArquivoAnexoItem[] = [];

      try {
        for (let i = 0; i < arquivosValidos.length; i++) {
          const file = arquivosValidos[i];
          const formData = new FormData();
          formData.append('arquivo', file);

          try {
            const url = sessionId ? `fileupload?sessionId=${sessionId}` : 'fileupload';
            const response = await api.post(url, formData);
            const data = response.data || {};
            enviados.push({
              nome: data.name || file.name,
              tamanhoBytes: file.size,
              arquivo_final: 'não',
              caminho: data.caminho || (data.tempFolder && data.name ? `${data.tempFolder}/${data.name}` : undefined),
              tempFolder: data.tempFolder,
            });
          } catch (erro: any) {
            const mensagemErro = erro?.response?.data?.message || erro?.response?.data?.error || `Falha ao enviar "${file.name}"`;
            toast.error(mensagemErro);
          }
        }

        if (enviados.length) setArquivos(prev => [...prev, ...enviados]);
      } finally {
        setIsUploading(false);
        setArquivosEnviando([]);
      }
    }

    e.currentTarget.value = '' as any;
  }, [arquivos, sessionId]);

  const removerArquivo = useCallback(async (caminho: string | undefined, nome: string) => {
    if (caminho) {
      try {
        const caminhoCodificado = caminho.split('/').map(s => encodeURIComponent(s)).join('/');
        await api.delete(`deletefile/${caminhoCodificado}`);
      } catch (erro) {
        console.error('Erro ao deletar arquivo temporário:', erro);
      }
    }
    setArquivos(prev => caminho ? prev.filter(a => a.caminho !== caminho) : prev.filter(a => a.nome !== nome));
  }, []);

  // Validação simples do step (RN32, RN34.1, RN34.2)
  const validar = useCallback(() => {
    const novosErros: Record<string, string> = {};
    const agora = new Date();

    const mkDate = (data: string, hora: string) => {
      if (!data || !hora) return null;
      const [y, m, d] = data.split('-').map(Number);
      const [hh, mm] = hora.split(':').map(Number);
      return new Date(y, m - 1, d, hh, mm);
    };

    const dtAceite = mkDate(dataAceite, horaAceite);
    const dtConclusao = mkDate(dataConclusao, horaConclusao);

    if (userProfile !== 'Solicitante' && !dtAceite) novosErros.prazo_aceite = 'Prazo para aceite é obrigatório';
    if (!dtConclusao) novosErros.prazo_para_conclusao = 'Prazo para conclusão é obrigatório';

    if (userProfile !== 'Solicitante' && dtAceite && dtAceite < agora) novosErros.prazo_aceite = 'Data/hora de aceite não pode ser passada';
    if (dtConclusao && dtConclusao < agora) novosErros.prazo_para_conclusao = 'Data/hora de conclusão não pode ser passada';

    // RN34.2: validar relação com Prazo Fatal, se disponível
    if (prazoFatal) {
      if (userProfile !== 'Solicitante' && dtAceite && dtAceite > prazoFatal) {
        novosErros.prazo_aceite = 'Aceite deve ser anterior ao Prazo Fatal';
      }
      if (dtConclusao && dtConclusao < prazoFatal) {
        novosErros.prazo_para_conclusao = 'Conclusão deve ser posterior ao Prazo Fatal';
      }
    }

    if (userProfile !== 'Solicitante' && dtAceite && dtConclusao && dtAceite.getTime() === dtConclusao.getTime()) {
      toast.error('Os prazos não podem ser iguais');
      return false;
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  }, [dataAceite, horaAceite, dataConclusao, horaConclusao, prazoFatal, userProfile]);

  useEffect(() => {
    if(validando && step === currentStep) {
      const r = validar();
      setValidando(false)
      if(r) {
        setCurrentStep(currentStep + 1);
      }
    }
  }, [validando]);

  // Expor validação para páginas de teste/integração via window para facilitar QA local
  useEffect(() => {
    (window as any).__validateStep4 = validar;
    return () => { delete (window as any).__validateStep4; };
  }, [validar]);

  // Notifica o componente pai sobre mudanças no estado de upload
  useEffect(() => {
    if (onUploadingChange) {
      onUploadingChange(isUploading);
    }
  }, [isUploading, onUploadingChange]);

  if (!isActive) return null;

  return (
    <StepContainer>
      <StepContent>
        {/* Seção 1: Prazos */}
        <SectionContainer>
          <StepHeader>  
            <HeaderContent>
              <StepTitle>Defina prazos para o atendimento!</StepTitle>
            </HeaderContent>
          </StepHeader>
          
          <FieldsRow>
            {userProfile !== 'Solicitante' && (
              <FieldGroup>
                <Label required>Prazo para aceite da Demanda:</Label>
                <DateTimeContainer>
                  <DatePickerContainer>
                    <DateTimePicker
                      value={valorAceiteCompleto}
                      onChange={onChangeAceite}
                      placeholder="dd/mm/aaaa | hh:mm"
                    />
                  </DatePickerContainer>
                  <TimezoneReadonly>{timezoneShort(fusoSelecionado)}</TimezoneReadonly>
                </DateTimeContainer>
                {erros.prazo_aceite && <ErrorMessage>{erros.prazo_aceite}</ErrorMessage>}
              </FieldGroup>
            )}

            <FieldGroup>
              <Label required>Prazo para conclusão da demanda:</Label>
              <DateTimeContainer>
                <DatePickerContainer>
                  <DateTimePicker
                    value={valorConclusaoCompleto}
                    onChange={onChangeConclusao}
                    placeholder="dd/mm/aaaa | hh:mm"
                  />
                </DatePickerContainer>
                <TimezoneReadonly>{timezoneShort(fusoSelecionado)}</TimezoneReadonly>
              </DateTimeContainer>
              {erros.prazo_para_conclusao && <ErrorMessage>{erros.prazo_para_conclusao}</ErrorMessage>}
            </FieldGroup>
          </FieldsRow>
        </SectionContainer>

        {/* Seção 2: Orientações e Documentos */}
        <SectionContainer>
          <SectionTitle>Oriente sobre a realização da demanda!</SectionTitle>
          <SectionDescription>
            Conte ao profissional informações relevantes a se considerar durante a realização da demanda.
          </SectionDescription>
          
          <FieldsRow>
            <FieldGroup>
              <Label>Orientações prévias:</Label>
              <TextArea
                placeholder="Digite as orientações..."
                value={orientacoes}
                onChange={(e) => setOrientacoes(e.target.value)}
                hasError={!!erros.orientacoes}
              />
              {erros.orientacoes && <ErrorMessage>{erros.orientacoes}</ErrorMessage>}
            </FieldGroup>

            <FieldGroup>
              <Label>Documentos:</Label>
              <FilesContainer>
                <label htmlFor="file-input" style={{ display: 'block', width: '100%', cursor: 'pointer' }}>
                  <AddFilesButton as="div">
                    <UploadIcon>↑</UploadIcon>
                    Adicionar arquivos
                  </AddFilesButton>
                </label>
                <HiddenFileInput
                  ref={fileInputRef}
                  id="file-input"
                  type="file"
                  multiple
                  onChange={handleFiles}
                />
                {arquivosEnviando.length > 0 && (
                  <>
                    {arquivosEnviando.map((file, index) => (
                      <LoadingFileRow key={`loading-${index}`}>
                        <LoadingFileInfo>
                          <LoadingFileName title={file.name}>{file.name}</LoadingFileName>
                          <LoadingFileSize>{formatFileSize(file.size)}</LoadingFileSize>
                        </LoadingFileInfo>
                        <LoadingSpinner>
                          <Spinner />
                        </LoadingSpinner>
                      </LoadingFileRow>
                    ))}
                  </>
                )}
                {arquivos.length > 0 && (
                  <FilesList>
                    {arquivos.map(a => (
                      <FileRow key={a.caminho || a.nome}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1, minWidth: 0 }}>
                          <FileName title={a.nome}>{a.nome}</FileName>
                          <span style={{ color: 'var(--c6b7280)', fontSize: 12, flexShrink: 0 }}>
                            {(a.tamanhoBytes / (1024 * 1024)).toFixed(2)} MB
                          </span>
                        </div>
                        <DeleteButton
                          onClick={(e) => {
                            e.stopPropagation();
                            removerArquivo(a.caminho, a.nome);
                          }}
                        >
                          <IconTrash />
                        </DeleteButton>
                      </FileRow>
                    ))}
                  </FilesList>
                )}
              </FilesContainer>
            </FieldGroup>
          </FieldsRow>
        </SectionContainer>

      </StepContent>
    </StepContainer>
  );
};

export default DadosFinal;


